import BaseWrapper from "../../components/BaseWrapper/BaseWrapper";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider } from "../../contexts/CreateRecordFormContext";
import RecordModal from "../../components/RecordModal/RecordModal";
import UsersConfig from "../../lib/config/users";
import Feedback from "../../components/Feedback/Feedback";
import { API } from "../../lib/api";
import UsersColumns from "../../lib/TableColumns/UsersColumns";
import { AddCircle, Construction, PersonOff, PersonAdd } from "@mui/icons-material";
import CollapsibleGridCard from "../../components/CollapsibleGridCard";

import { Alert, Button } from "@mui/material";

const Users = () => {
    const [seats, setSeats] = useState(null);
    const [seatError, setSeatError] = useState(false);
    const [users, setUsers] = useState([]) 
    const [selectedRows, setSelectedRows] = useState([]);
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [openUpdateModal, setOpenUpdateModal] = useState(false);
    const [feedback, setFeedback] = useState({open: false, message: "", type: ""}) 

    const refreshUsers = useCallback(async () => {
        try {
            const [rows, usage] = await Promise.all([API.Users.FetchAll(), API.Users.FetchSeats()]);
            setUsers(rows);
            setSeats(usage);
            setSeatError(false);
        } catch {
            setSeats(null);
            setSeatError(true);
        }
    }, []);
    const changeActive = async (isActive) => {
        const response = await API.Users.Update(selectedRows.map(_id => ({ _id, data: { isActive } })));
        setFeedback({ open: true, message: response.data.message, type: response.status === 200 ? 'success' : 'error' });
        await refreshUsers();
    };
    // -- Columns saved in lib/TableColumns for readability -- //
    const columns = UsersColumns()
    const selectedUser = useMemo(
        () => selectedRows.length === 1
            ? users.find((user) => String(user._id) === String(selectedRows[0])) || null
            : null,
        [selectedRows, users]
    )
    const updateInitialValues = selectedUser
        ? {
            email: selectedUser.email || "",
            firstName: selectedUser.firstName || "",
            lastName: selectedUser.lastName || "",
            role: selectedUser.role || null,
            department: selectedUser.department || "",
            canAuditAllChats: !!selectedUser.canAuditAllChats,
        }
        : {}
    
    // -- Foreces re-render by updating users state when a new user is created -- //
    const addUser = (newUser) => setUsers(prevUsers => [...prevUsers, newUser]);

    // -- Keep track of selected rows -- //
    const handleSelectionChange = (selection) => setSelectedRows(selection)

    // -- State Management for Modal that creates a new user -- //
    const handleOpenCreateModal = () => seats?.canAdd && setOpenCreateModal(true);
    const handleCloseCreateModal = () => setOpenCreateModal(false);
    
    // -- State Management for Modal that updates a existing user(s) -- //
    const handleOpenUpdateModal = () => selectedRows.length ? setOpenUpdateModal(true) : setFeedback({open: true, message: "Select at least one record to update!"})
    const handleCloseUpdateModal = () => setOpenUpdateModal(false);


    // -- API Request to Create a new User -- //
    const handleCreateSubmit = async (data) => {
        const response = await API.Users.Create(data)

        if (response.status === 200) {
            addUser(response.data.data)
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: 'success' }))
        } else {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: 'error' }))
        }

        await refreshUsers();
        handleCloseCreateModal()
    };

    // -- API Request to Update a User -- //
    const handleUpdateSubmit = async (userData) => {

        const updatedFields = Object.fromEntries(
            Object.entries(userData).filter(([_, value]) => value !== "" && value !== undefined && value !== null)
        );
        const response = await API.Users.Update( selectedRows.map(_id => ({ _id, data: updatedFields })) );

        if (response.status === 200) {

            const selectedIds = new Set(selectedRows.map(id => String(id)));

            setUsers(prev => prev.map(user => selectedIds.has(String(user._id)) ? { ...user, ...updatedFields } : user ) );
            setFeedback(prev => ( {...prev, open: true, message: response.data.message, type: "success"} ));
        } else {
            setFeedback(prev => ({...prev, open: true, message: response?.data?.message || "Error updating user(s)", type: "error"}));
        }

        await refreshUsers();
        handleCloseUpdateModal(false);
    };

    // -- API Request to Delete user(s) -- //
    const handleDeleteSubmit = async () => {
        const response = await API.Users.Delete(selectedRows)
        if (response.status === 200) {
            setUsers(prevUsers => prevUsers.filter(user => !selectedRows.includes(user._id)) );
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: 'success' }))
        } else {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: 'error' }))
        }

        await refreshUsers();
        setSelectedRows([]) 
        handleCloseUpdateModal()
    }

    // -- Define buttons for ActionButtons component -- //
    const actionButtons = [
        { title: "Deactivate selected users (frees seats)", disabled: !selectedRows.length, onClick: () => changeActive(false), icon: PersonOff },
        { title: "Reactivate selected users", disabled: !selectedRows.length, onClick: () => changeActive(true), icon: PersonAdd },
        { title: seats?.canAdd ? "Create User" : "No user seats available", disabled: !seats?.canAdd, onClick: handleOpenCreateModal, icon: AddCircle, color: "primary" },
        { title: "Update Selected Record(s)", onClick: handleOpenUpdateModal, icon: Construction, color: "primary" },
    ];

    // -- Fetch Users Data -- //
    useEffect(() => { refreshUsers(); }, [refreshUsers]);

    return (
        <BaseWrapper content={
            <FormProvider>
                <Alert severity={seatError || (seats && !seats.canAdd) ? 'warning' : 'info'} sx={{ mb: 2 }}
                    action={<Button color="inherit" onClick={refreshUsers}>Refresh</Button>}>
                    {seatError ? 'Unable to load user seats. Refresh to try again.' : seats
                        ? seats.planName + ': ' + seats.used + ' / ' + seats.limit + ' active users. ' + (seats.canAdd ? 'Administrators count toward this limit.' : 'Deactivate a user or contact your system administrator to upgrade.')
                        : 'Loading user seats...'}
                    {' Inactive users cannot sign in; their records are retained.'}
                </Alert>
                <Feedback props={feedback} setFeedback={setFeedback} />
                <RecordModal open={openCreateModal} handleClose={handleCloseCreateModal} fields={UsersConfig.createUserFields} title="Create New User" onSubmit={handleCreateSubmit} />
                <RecordModal open={openUpdateModal} handleClose={handleCloseUpdateModal} fields={UsersConfig.updateUserFields} title="Update User" onSubmit={handleUpdateSubmit} onDelete={handleDeleteSubmit} isUpdate={true} initialValues={updateInitialValues} />
                <CollapsibleGridCard
                    title="Administration"
                    linkLabel="Users"
                    rows={users}
                    columns={columns}
                    getRowId={(row) => row._id}
                    checkboxSelection
                    rowSelectionModel={selectedRows}
                    onRowSelectionModelChange={handleSelectionChange}
                    actionButtons={actionButtons}
                    initialPageSize={10}
                    pageSizeOptions={[10, 25, 50]}
                />
            </FormProvider>              
        } />
    )
}

export default Users
